import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Container from '@Theme/natucare/components/Container/Container';
import Picture from '@Theme/natucare/components/Picture/Picture';
import useEmblaCarousel from 'embla-carousel-react';
import { ArrowLeftIcon, ArrowRightIcon } from '@App/common/components/icons/NavigationArrows';
import * as styles from './Blog.module.scss';
const SLIDER_BREAKPOINT = 1200;
const ArticleCard = ({
  article,
  className = '',
  isFeatured = false
}) => {
  if (!article) {
    return null;
  }
  const {
    heading,
    description,
    link,
    image,
    category,
    linkLabel
  } = article;
  const mobileSource = (image === null || image === void 0 ? void 0 : image.mobile) || {};
  const desktopSource = (image === null || image === void 0 ? void 0 : image.desktop) || {};
  return /*#__PURE__*/React.createElement("article", {
    className: `${styles.article} ${isFeatured ? styles.articleFeatured : styles.articleList} ${className}`
  }, image && /*#__PURE__*/React.createElement(Picture, {
    xs: {
      avif: mobileSource.avif,
      webp: mobileSource.webp,
      src: mobileSource.src,
      width: mobileSource.width,
      height: mobileSource.height
    },
    md: {
      avif: desktopSource.avif,
      webp: desktopSource.webp,
      src: desktopSource.src,
      width: desktopSource.width,
      height: desktopSource.height
    },
    lg: {
      avif: desktopSource.avif,
      webp: desktopSource.webp,
      src: desktopSource.src,
      width: desktopSource.width,
      height: desktopSource.height
    },
    alt: (image === null || image === void 0 ? void 0 : image.alt) || '',
    pictureClassname: `${styles.articlePicture} ${isFeatured ? styles.articlePictureFeatured : styles.articlePictureList}`,
    imgClassname: styles.articleImage
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.articleBody
  }, isFeatured && category && /*#__PURE__*/React.createElement("p", {
    className: styles.articleCategory
  }, category), heading && /*#__PURE__*/React.createElement("h3", {
    className: `${styles.articleHeading} ${isFeatured ? styles.articleHeadingFeatured : ''}`
  }, link ? /*#__PURE__*/React.createElement("a", {
    className: `${styles.articleLink} ${isFeatured ? styles.articleLinkFeatured : ''}`,
    href: link,
    dangerouslySetInnerHTML: {
      __html: heading
    }
  }) : /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: heading
    }
  })), description && /*#__PURE__*/React.createElement("p", {
    className: `${styles.articleDescription} ${isFeatured ? styles.articleDescriptionFeatured : ''}`,
    dangerouslySetInnerHTML: {
      __html: description
    }
  }), isFeatured && link && /*#__PURE__*/React.createElement("a", {
    className: styles.articleCta,
    href: link
  }, linkLabel || 'Czytaj więcej')));
};
const Blog = ({
  heading,
  tabs,
  featuredArticle
}) => {
  if (!(tabs !== null && tabs !== void 0 && tabs.length)) {
    return null;
  }
  const {
    0: activeTabIndex,
    1: setActiveTabIndex
  } = useState(0);
  const {
    0: isSliderMode,
    1: setIsSliderMode
  } = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.innerWidth < SLIDER_BREAKPOINT;
  });
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleResize = () => {
      setIsSliderMode(window.innerWidth < SLIDER_BREAKPOINT);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const safeActiveIndex = useMemo(() => {
    const lastIndex = ((tabs === null || tabs === void 0 ? void 0 : tabs.length) || 1) - 1;
    return Math.min(activeTabIndex, Math.max(lastIndex, 0));
  }, [activeTabIndex, tabs]);
  const activeTab = useMemo(() => tabs === null || tabs === void 0 ? void 0 : tabs[safeActiveIndex], [tabs, safeActiveIndex]);
  const handleTabClick = useCallback(index => {
    setActiveTabIndex(index);
  }, []);
  const articles = (activeTab === null || activeTab === void 0 ? void 0 : activeTab.articles) || [];
  const highlightedArticle = featuredArticle || articles[0] || null;
  const listArticles = useMemo(() => {
    if (!articles.length) {
      return [];
    }
    if (featuredArticle) {
      return articles;
    }
    return articles.slice(1);
  }, [articles, featuredArticle]);
  const featuredWithCategory = !isSliderMode && highlightedArticle ? {
    ...highlightedArticle,
    category: activeTab === null || activeTab === void 0 ? void 0 : activeTab.label
  } : null;
  const sliderArticles = useMemo(() => {
    if (!articles.length) {
      return [];
    }
    if (featuredArticle) {
      return articles;
    }
    const merged = highlightedArticle ? [highlightedArticle].concat(_toConsumableArray(listArticles)) : listArticles;
    return merged.filter(Boolean);
  }, [articles, featuredArticle, highlightedArticle, listArticles]);
  const sliderSlides = useMemo(() => sliderArticles.map((article, index) => /*#__PURE__*/React.createElement("div", {
    className: styles.rootSlide,
    key: `${(article === null || article === void 0 ? void 0 : article.heading) || 'mobile-article'}-${index}`
  }, /*#__PURE__*/React.createElement(ArticleCard, {
    article: article
  }))), [sliderArticles]);
  const sliderOptions = useMemo(() => ({
    align: 'start',
    containScroll: 'trimSnaps',
    skipSnaps: false,
    loop: false
  }), []);
  const shouldRenderSlider = isSliderMode && sliderSlides.length > 0;
  const shouldRenderList = !isSliderMode && listArticles.length > 0;
  const sliderKey = useMemo(() => `${safeActiveIndex}-${sliderArticles.map(article => (article === null || article === void 0 ? void 0 : article.heading) || '').join('|')}`, [safeActiveIndex, sliderArticles]);
  const [emblaRef, emblaApi] = useEmblaCarousel(sliderOptions);
  const {
    0: canScrollPrev,
    1: setCanScrollPrev
  } = useState(false);
  const {
    0: canScrollNext,
    1: setCanScrollNext
  } = useState(false);
  const updateNavigationState = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);
  useEffect(() => {
    if (!emblaApi) {
      return undefined;
    }
    updateNavigationState();
    emblaApi.on('select', updateNavigationState);
    emblaApi.on('reInit', updateNavigationState);
    emblaApi.on('resize', updateNavigationState);
    return () => {
      emblaApi.off('select', updateNavigationState);
      emblaApi.off('reInit', updateNavigationState);
      emblaApi.off('resize', updateNavigationState);
    };
  }, [emblaApi, updateNavigationState]);
  useEffect(() => {
    if (!emblaApi) {
      return undefined;
    }
    emblaApi.reInit(sliderOptions);
    emblaApi.scrollTo(0, true);
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(updateNavigationState);
    } else {
      updateNavigationState();
    }
    return undefined;
  }, [emblaApi, sliderKey, sliderSlides.length, sliderOptions, updateNavigationState]);
  const handlePrev = useCallback(() => {
    emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollPrev();
  }, [emblaApi]);
  const handleNext = useCallback(() => {
    emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollNext();
  }, [emblaApi]);
  return /*#__PURE__*/React.createElement("section", {
    className: styles.root
  }, /*#__PURE__*/React.createElement(Container, {
    classname: styles.rootContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.rootLeft
  }, heading && /*#__PURE__*/React.createElement("h2", {
    className: styles.rootHeading,
    dangerouslySetInnerHTML: {
      __html: heading
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.rootTabsRow
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.rootTabs,
    role: "tablist",
    "aria-label": heading || 'Blog categories'
  }, tabs.map(({
    label
  }, index) => {
    const isActive = index === safeActiveIndex;
    return /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: label,
      role: "tab",
      "aria-selected": isActive,
      className: `${styles.rootTab} ${isActive ? styles.rootTabActive : ''}`,
      onClick: () => handleTabClick(index)
    }, label);
  }))), shouldRenderSlider && /*#__PURE__*/React.createElement("div", {
    className: styles.rootCarousel,
    key: sliderKey
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.rootCarouselViewport,
    ref: emblaRef
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.rootCarouselContainer
  }, sliderSlides)), sliderSlides.length > 1 && /*#__PURE__*/React.createElement("div", {
    className: styles.rootCarouselArrows
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: `${styles.rootCarouselArrow} ${styles.rootCarouselArrowPrev}`,
    onClick: handlePrev,
    disabled: !canScrollPrev,
    "aria-label": "Poprzedni artyku\u0142"
  }, /*#__PURE__*/React.createElement(ArrowLeftIcon, null)), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: `${styles.rootCarouselArrow} ${styles.rootCarouselArrowNext}`,
    onClick: handleNext,
    disabled: !canScrollNext,
    "aria-label": "Nast\u0119pny artyku\u0142"
  }, /*#__PURE__*/React.createElement(ArrowRightIcon, null)))), shouldRenderList && /*#__PURE__*/React.createElement("div", {
    className: styles.rootArticles
  }, listArticles.map((article, index) => /*#__PURE__*/React.createElement(ArticleCard, {
    key: `${(article === null || article === void 0 ? void 0 : article.heading) || 'article'}-${index}`,
    article: article,
    className: styles.rootArticle
  })))), featuredWithCategory && /*#__PURE__*/React.createElement("aside", {
    className: styles.rootRight
  }, /*#__PURE__*/React.createElement(ArticleCard, {
    article: featuredWithCategory,
    isFeatured: true
  }))));
};
export default Blog;